// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Copy, MoreHorizontal, Pencil } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SettingsCompareIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { cn } from "@/lib/utils";

interface ChainActionsMenuProps {
  onDuplicate: () => void;
  onCompareSettings: () => void;
  /** Adds Edit at the top of the menu. The list rows pass it; the detail header has its own Edit button. */
  onEdit?: () => void;
  triggerClassName?: string;
}

export function ChainActionsMenu({ onDuplicate, onCompareSettings, onEdit, triggerClassName }: ChainActionsMenuProps) {
  const { t } = useTranslation("back-office");
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.chainsEdit);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className={cn("h-8 w-8", triggerClassName)}>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={-32} className="dropdown-overlap">
        {onEdit && canEdit && (
          <>
            <DropdownMenuItem onSelect={onEdit}>
              <Pencil className="h-4 w-4" />
              {t("buttons.edit")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        {canEdit && (
          <DropdownMenuItem onSelect={onDuplicate}>
            <Copy className="h-4 w-4" />
            {t("chains.list.actions.duplicate")}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={onCompareSettings}>
          <SettingsCompareIcon className="h-4 w-4" />
          {t("chains.list.actions.compareSettings")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
